import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Volume2, XCircle } from "lucide-react";
import { useLang } from "@/lib/content";
import { api } from "@/lib/api";
import { speak } from "@/lib/tts";

const norm = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[.,!?;:"'()]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export default function DictationTool() {
  const { lang } = useLang();
  const [sentences, setSentences] = useState([]);
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState(null);
  const [streak, setStreak] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    api("/study/dictation").then((d) => setSentences(d.sentences)).catch(() => {});
  }, []);

  const s = sentences[idx];

  const play = async () => {
    if (!s) return;
    setPlaying(true);
    try {
      const a = await speak(s.en);
      a.onended = () => setPlaying(false);
    } catch (e) {
      setPlaying(false);
    }
  };

  const check = () => {
    const ok = norm(typed) === norm(s.en);
    setResult(ok ? "ok" : "bad");
    if (ok) setStreak((v) => v + 1);
    else setStreak(0);
    setRevealed(true);
  };

  const next = () => {
    setIdx((v) => (v + 1) % sentences.length);
    setTyped("");
    setResult(null);
    setRevealed(false);
  };

  return (
    <section id="escritura" className="bg-paperdeep py-24">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
            {lang === "es" ? "Dictado y escritura" : "Dictation & writing"}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {lang === "es" ? "Las frases que " : "The sentences they "}
            <span className="italic text-heritage">{lang === "es" ? "sí te dictarán" : "will actually read"}</span>
          </h2>
          <p className="mt-4 text-inkbody">
            {lang === "es"
              ? "Entrena el oído con las frases más comunes del examen, hechas con el vocabulario oficial de USCIS. Escucha, escribe y comprueba."
              : "Train your ear with the most common test sentences, built from official USCIS vocabulary. Listen, type, and check."}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="dictation-widget"
          className="mx-auto mt-10 max-w-2xl rounded-3xl border border-line bg-white p-7 shadow-xl sm:p-9"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-heritage">
              {lang === "es" ? `Frase ${idx + 1} de ${sentences.length}` : `Sentence ${idx + 1} of ${sentences.length}`}
            </span>
            {streak > 0 && (
              <span className="rounded-full bg-emeraldsucc/10 px-3.5 py-1.5 text-xs font-bold text-emeraldsucc" data-testid="dictation-streak">
                {lang === "es" ? `Sin errores: ${streak}` : `Streak: ${streak}`}
              </span>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              data-testid="dictation-play"
              onClick={play}
              className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-paper transition-all hover:-translate-y-0.5"
            >
              <Volume2 size={16} /> {playing ? (lang === "es" ? "Reproduciendo…" : "Playing…") : lang === "es" ? "Escuchar en inglés" : "Listen in English"}
            </button>
            <button
              data-testid="dictation-reveal"
              onClick={() => setRevealed((v) => !v)}
              className="text-xs font-bold text-mutedtext underline-offset-4 hover:underline"
            >
              {revealed ? (lang === "es" ? "Ocultar frase" : "Hide sentence") : lang === "es" ? "Ver frase (trampa)" : "Peek at the sentence"}
            </button>
          </div>

          {revealed && s && (
            <div className="mt-4 rounded-xl bg-paperdeep/70 p-4" data-testid="dictation-reveal-box">
              <p className="text-base font-bold text-navy">{s.en}</p>
              <p className="mt-1 text-sm text-mutedtext">{s.es}</p>
            </div>
          )}

          <input
            data-testid="dictation-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && s && !result && check()}
            placeholder={lang === "es" ? "Escribe lo que escuchas…" : "Type what you hear…"}
            className="field mt-5 text-base"
          />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button
              data-testid="dictation-check"
              onClick={check}
              disabled={!s || result === "ok"}
              className="rounded-full bg-heritage px-7 py-3 text-sm font-bold text-paper transition-all hover:bg-heritagedark disabled:opacity-50"
            >
              {lang === "es" ? "Comprobar" : "Check"}
            </button>
            {result && (
              <button
                data-testid="dictation-next"
                onClick={next}
                className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-paper"
              >
                {lang === "es" ? "Siguiente frase" : "Next sentence"} <ArrowRight size={14} />
              </button>
            )}
          </div>

          <AnimatePresence>
            {result && s && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid="dictation-feedback"
                className={`mt-5 flex items-start gap-2.5 rounded-xl p-4 text-sm font-semibold ${result === "ok" ? "bg-emeraldsucc/10 text-emeraldsucc" : "bg-heritage/10 text-heritagedark"}`}
              >
                {result === "ok" ? <CheckCircle2 size={17} className="mt-0.5 shrink-0" /> : <XCircle size={17} className="mt-0.5 shrink-0" />}
                <div>
                  {result === "ok" ? (
                    <p>{lang === "es" ? "¡Perfecto! Así de simple es el dictado del examen." : "Perfect! The test dictation really is this simple."}</p>
                  ) : (
                    <p>
                      {lang === "es" ? "Casi. La frase era:" : "Almost. The sentence was:"} <span className="font-bold">{s.en}</span> — {lang === "es" ? "escúchala otra vez y reintenta." : "listen again and retry."}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
